import React from "react";

/**
 * PaperMarginalia — procedurally generates the physical artifacts
 * that make each dossier folder feel like a unique case file
 * handled by different investigators over decades.
 *
 * All artifacts are DETERMINISTIC per dossier slug — the same
 * dossier always renders identically, but different dossiers get
 * different combinations of stickers, tabs, notes, wear marks.
 *
 * SAFETY: every artifact lives in the paper folder's OUTER
 * padding (top/right/bottom rails) so nothing crosses title,
 * category, ref, analytics, verification labels, toolbar, or
 * votes strip. Marginalia is hidden entirely on viewports ≤720px.
 *
 * Handwritten notes are CONTEXT-AWARE: matched first against a
 * curated per-topic dictionary (JFK → ballistics; Tuskegee →
 * consent records; Paperclip → recruitment files; etc.), then
 * against the dossier's category, then falls back to a generic
 * archivist pool.
 */

const _hash = (s) => {
  let h = 2166136261;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  }
  return h >>> 0;
};

/* -------------------------------------------------------------
 * Context-aware note dictionary.
 * Keys are lowercase substrings — matched against the slug or
 * title. First match wins; if none match, we fall back to
 * category-based, then generic archivist notes.
 * ----------------------------------------------------------- */
const TOPIC_NOTES = {
  jfk:              ["Compare witness statements.", "Ballistics?", "Need Zapruder timing.", "FBI report incomplete?"],
  kennedy:          ["Warren Commission gaps.", "Ballistics?", "Compare motorcade testimony."],
  tuskegee:         ["Verify CDC timeline.", "Consent records?", "Compare survivor testimony.", "PHS memo 1932."],
  paperclip:        ["Cross-check recruitment files.", "Military intelligence?", "See declassified memo.", "Von Braun status."],
  kilner:           ["Compare Crookes experiments.", "Optical artifact?", "Locate original plates.", "Dicyanin filter used?"],
  aura:             ["Compare Crookes experiments.", "Optical artifact?", "Peer review?"],
  dyatlov:          ["Weather records.", "Avalanche disputed.", "Check autopsy.", "Radiation levels?"],
  titanic:          ["Insurance angle?", "Coal fire reports.", "Compare survivor accounts.", "Ismay testimony."],
  roswell:          ["Original newspaper?", "Witness timeline.", "Mac Brazel affidavit.", "Photograph provenance."],
  mincemeat:        ["Corpse identity confirmed?", "German response timeline.", "MI5 diary.", "Compare naval intel."],
  mkultra:          ["LSD trial subjects?", "Church committee files.", "Gottlieb correspondence.", "Compare CIA memos."],
  watergate:        ["Rose Mary Woods gap.", "Follow the money.", "Deep Throat identity?", "Cross-check tape log."],
  "iran-contra":    ["Follow the money.", "North testimony?", "Reagan diary.", "Compare Poindexter memo."],
  gulf:             ["Compare Navy logs.", "Torpedo report?", "Verify chain of command.", "Casus belli timing?"],
  tonkin:           ["Compare Navy logs.", "Torpedo report?", "McNamara testimony.", "NSA intercepts."],
  havana:           ["Compare medical exams.", "Frequency analysis?", "Verify onset timeline.", "Personnel roster."],
  snowden:          ["Verify PRISM slides.", "Compare NSA memo.", "Cross-check dates.", "Journalist source chain."],
  patriot:          ["FOIA request.", "Compare surveillance orders.", "Timeline vs. 9/11."],
  epstein:          ["Flight logs.", "Compare witness statements.", "Verify autopsy report.", "Prosecutor decisions."],
  jeffrey:          ["Flight logs.", "Verify autopsy report.", "Compare witness statements."],
  9:                ["Compare tower timeline.", "Steel fatigue analysis.", "FOIA insurance filings."],
  northwoods:       ["Original Lemnitzer memo.", "Rejected — by whom?", "Compare NSC files."],
  cointelpro:       ["Compare Hoover memos.", "Field office logs.", "Verify surveillance targets."],
  fbi:              ["Field office logs.", "Compare Hoover memos.", "Chain of custody."],
  cia:              ["Compare Langley memo.", "Chain of custody.", "Verify station chief."],
  nsa:              ["Verify intercept logs.", "Compare declassified memo.", "Cross-check dates."],
  chernobyl:        ["Reactor logs.", "Compare Soviet reports.", "Iodine measurements?", "Liquidator manifest."],
  challenger:       ["O-ring temperature.", "Feynman testimony.", "Compare NASA memos.", "Morton Thiokol call."],
  bhopal:           ["Compare plant records.", "Chemical inventory.", "Verify death toll.", "Union Carbide memo."],
  fukushima:        ["Reactor logs.", "TEPCO timeline.", "Compare regulatory reports."],
  banking:          ["Follow the money.", "Cross-check ledger.", "Wire transfer records?", "Compare bank memos."],
  federal:          ["Follow the money.", "Cross-check ledger.", "Verify board minutes."],
  reserve:          ["Follow the money.", "Cross-check ledger.", "Verify board minutes."],
  vaccine:          ["Compare trial data.", "Verify FDA correspondence.", "Adverse event reports."],
  fluoride:         ["Compare municipal records.", "Verify original studies.", "Dose-response data?"],
  chemtrail:        ["Compare flight logs.", "Air sample data?", "Verify meteorology reports."],
  reptilian:        ["Verify claim provenance.", "Compare witness statements.", "Cross-check sources."],
  flat:             ["Verify claim provenance.", "Compare peer-reviewed studies.", "Original source?"],
  moon:             ["Compare NASA transmission logs.", "Verify photograph provenance.", "Independent tracking data?"],
  apollo:           ["Compare NASA transmission logs.", "Verify photograph provenance.", "Third-party tracking data."],
  bigfoot:          ["Compare witness statements.", "Verify footprint analysis.", "Original photograph."],
  loch:             ["Verify photograph provenance.", "Compare witness statements.", "Sonar data?"],
  ness:             ["Verify photograph provenance.", "Compare witness statements.", "Sonar data?"],
  chappaquiddick:   ["Compare witness statements.", "Timeline vs. police report.", "Verify autopsy."],
  wtc:              ["Compare tower timeline.", "Steel fatigue analysis.", "FOIA insurance filings."],
  bilderberg:       ["Attendee roster verified?", "Compare press coverage.", "Follow the money."],
  bohemian:         ["Attendee roster verified?", "Compare press coverage.", "Verify ceremony descriptions."],
  rothschild:       ["Follow the money.", "Compare estate records.", "Verify banking ledger."],
  algorithmic:      ["Compare training data leaks.", "Verify audit reports.", "Cross-check bias studies."],
  bigtech:          ["Compare internal memos.", "Verify audit reports.", "FOIA regulator filings."],
  ai:               ["Compare model cards.", "Verify audit reports.", "Cross-check bias studies."],
  gary:             ["Compare autopsy report.", "Verify witness statements.", "Chain of custody."],
  webb:             ["Compare autopsy report.", "Verify witness statements.", "Follow the source chain."],
  khashoggi:        ["Compare consulate logs.", "Verify audio provenance.", "Chain of custody."],
  pinto:            ["Compare Ford memos.", "Verify NHTSA reports.", "Cost-benefit analysis."],
  pfas:             ["Compare 3M internal memos.", "Verify contamination data.", "Cross-check regulator filings."],
  "3m":             ["Compare 3M internal memos.", "Verify contamination data.", "Cross-check regulator filings."],
  afghanistan:      ["Compare Pentagon briefings.", "Verify field reports.", "Cross-check press releases."],

  // ---- Feb 2026 Archive Expansion (v3) — topic-specific notes ----
  // Each key is the full dossier slug so the substring resolver in
  // _resolveNotes matches only that dossier. Notes are short,
  // investigative, evidence-first — the voice of an analyst who's
  // already read the file and jotted the next thing to check.
  "pine-gap":                  ["Nautilus imaging update?", "Cross-check 'RAINFALL' slides.", "Verify 1966 treaty annexes.", "Peace Pilgrims trial exhibits."],
  "camp-x":                    ["Fleming presence — confirmed?", "HS-series SOE files.", "Hydra traffic logs?", "Cross-check OSS records."],
  "somerton-man":              ["Rubaiyat provenance?", "Adelaide DNA report v.2.", "Toxin ever identified?", "Cross-check Jestyn ties."],
  "newgrange":                 ["Verify solstice azimuth.", "O'Kelly 1967 field notes.", "Radiocarbon dates?", "Kerbstone K1 iconography."],
  "avro-cf-105-arrow":         ["Diefenbaker cabinet minutes.", "Cross-check destruction order.", "RL-206 nose section — LAC.", "BOMARC pressure documented?"],
  "the-bloop":                 ["PMEL hydrophone map.", "Compare Scotia icequakes.", "Verify Dziak 2005 paper.", "Source triangulation gap?"],
  "the-troubles":              ["Saville Report vol. VIII.", "Cross-check CAIN casualty DB.", "Ballast Inquiry exhibits.", "GFA referendum figures."],
  "harold-holt-disappearance": ["Cheviot Beach tide charts.", "Compare Gillespie statement.", "2005 coronial finding.", "ASIO cables — declassified?"],
  "shag-harbour-ufo-incident": ["HMCS Granby dive report.", "RCMP file — ATIA copy.", "Compare witness statements.", "DND classification 'UFO'."],
};

/* Category-based fallback pool — matches when the slug didn't hit
   any topic keyword. Preserves the archivist voice while keeping
   the note domain-appropriate. */
const CATEGORY_NOTES = {
  HEALTH:      ["Verify medical records.", "Consent documented?", "Cross-check CDC timeline.", "Compare longitudinal data.", "Verify autopsy."],
  MEDICAL:     ["Verify medical records.", "Consent documented?", "Compare longitudinal data.", "Verify autopsy."],
  HISTORY:     ["Cross-reference archives.", "Primary source?", "Timeline conflict?", "Verify witness statement.", "Compare declassified memo."],
  POLITICS:    ["Follow the money.", "Who else knew?", "Congressional record.", "Off-the-record confirmed?", "Compare press coverage."],
  SCIENCE:     ["Replicate experiment?", "Original data.", "Peer review?", "Independent verification.", "Compare lab notebooks."],
  TECHNOLOGY:  ["Compare source code.", "Verify audit report.", "Cross-check disclosures.", "FOIA regulator filings."],
  CONSPIRACY:  ["Follow the money.", "Missing witness?", "Timeline conflict.", "FOIA request.", "Verify original document."],
  MILITARY:    ["Cross-check unit logs.", "Verify chain of command.", "Compare after-action reports.", "Declassified memo?"],
  BUSINESS:    ["Follow the money.", "Compare internal memos.", "Verify SEC filings.", "Cross-check board minutes."],
  ECONOMY:     ["Follow the money.", "Compare ledger entries.", "Verify SEC filings.", "Cross-check central bank records."],
  ENVIRONMENT: ["Compare contamination data.", "Verify regulator filings.", "Cross-check independent studies."],
  RELIGION:    ["Verify original texts.", "Compare translations.", "Cross-check historical accounts."],
  UNCLASSIFIED:["Cross-check dates.", "Verify witness statement.", "Follow the money.", "Compare declassified memo."],
};

/* Generic archivist pool — final fallback, always appropriate. */
const GENERIC_NOTES = [
  "Cross-check dates.",
  "Verify witness statement.",
  "Follow the money.",
  "Compare declassified memo.",
  "Missing appendix?",
  "FOIA request.",
  "Needs second review.",
  "Timeline conflict.",
  "Who else knew?",
  "Reviewed. Solid.",
  "Corroborate w/ AP wire.",
];

/* Archive-box IDs — used for the ARCHIVE-BOX kraft card inline
   in the folder (see TopicHeader.jsx) and the vertical filing
   tab that protrudes off the right edge. */
const CATALOG_LABELS = [
  "A-417", "B-092", "C-208", "D-014", "A-663", "B-741",
  "C-355", "D-129", "A-901", "B-508", "C-072", "D-236",
];
const SHELF_LABELS = ["SHELF 01", "SHELF 02", "SHELF 03", "SHELF 04", "SHELF 07", "SHELF 09", "SHELF 12"];
const DECLASSIFY_YEARS = [1978, 1985, 1992, 1996, 2001, 2007, 2013, 2019, 2022];
const DECLASSIFY_MONTHS = ["JAN", "MAR", "APR", "MAY", "JUN", "AUG", "OCT", "NOV"];
const RECEIVED_OFFICES = [
  "BRITISH WAR OFFICE",
  "US NATIONAL ARCHIVES",
  "CENTRAL RECORDS OFFICE",
  "DEPT OF STATE RECORDS",
  "MI5 RECORDS UNIT",
  "CIA HISTORICAL COLLECTIONS",
  "SOVIET STATE ARCHIVE",
  "SCOTLAND YARD RECORDS",
];
const RECEIVED_CITIES = ["LONDON", "WASHINGTON", "LANGLEY", "MOSCOW", "GENEVA", "BERLIN"];
const INITIALS = ["JHM", "PKR", "MLC", "AER", "DBW", "TSH", "RFN", "CJL", "EMH", "GBR"];
const REC_YEARS = [1978, 1981, 1985, 1989, 1992, 1996, 2001, 2007, 2013, 2019];
const CUSTODY_UNITS = [
  "// SEC-A / RM-3", "// SEC-B / RM-7", "// SEC-D / RM-2",
  "// SEC-C / RM-9", "// SEC-A / RM-1", "// SEC-E / RM-4",
];

const _pickN = (seed, arr, n = 1) => {
  const h = _hash(seed);
  const out = [];
  const used = new Set();
  for (let i = 0; i < n && used.size < arr.length; i++) {
    let idx = (h + i * 37) % arr.length;
    while (used.has(idx)) idx = (idx + 1) % arr.length;
    used.add(idx);
    out.push(arr[idx]);
  }
  return out;
};

/**
 * Public helper — returns the deterministic archival provenance
 * values for a dossier so TopicHeader can render inline elements
 * (DECLASSIFIED stamp + ARCHIVE-BOX card + top file-tab) that
 * stay in sync with the rest of PaperMarginalia's artifacts.
 */
export const useDossierProvenance = (slug, ref) => {
  const seed = slug || ref || "dossier";
  const h = _hash(seed);
  const catalog  = _pickN(`${seed}-cat`, CATALOG_LABELS, 1)[0];
  const shelf    = _pickN(`${seed}-shelf`, SHELF_LABELS, 1)[0];
  const decYear  = _pickN(`${seed}-decy`, DECLASSIFY_YEARS, 1)[0];
  const decMonth = _pickN(`${seed}-decm`, DECLASSIFY_MONTHS, 1)[0];
  const decDay   = String(((h >> 3) % 28) + 1).padStart(2, "0");
  const office   = _pickN(`${seed}-office`, RECEIVED_OFFICES, 1)[0];
  const city     = _pickN(`${seed}-city`, RECEIVED_CITIES, 1)[0];
  const recYear  = _pickN(`${seed}-recy`, REC_YEARS, 1)[0];
  const recMonth = _pickN(`${seed}-recm`, DECLASSIFY_MONTHS, 1)[0];
  const recDay   = String(((h >> 7) % 28) + 1).padStart(2, "0");
  return {
    catalog,           // "A-417"
    shelf,             // "SHELF 03"
    declassifyDate: `${decDay} ${decMonth} ${decYear}`,
    receivedOffice: office,
    receivedCity:   city,
    receivedDate:   `${recDay} ${recMonth} ${recYear}`,
    // Deterministic artifact toggles for the "photorealistic subset"
    hasFingerprint: (h >> 17) % 3 === 0,
    hasCircularStamp: (h >> 19) % 2 === 0,
  };
};

/** Return the note pool most appropriate to this dossier.
 *  Match precedence: slug/title keyword → category → generic. */
const _resolveNotes = (slug, title, category) => {
  const hay = `${String(slug || "").toLowerCase()} ${String(title || "").toLowerCase()}`;
  for (const key of Object.keys(TOPIC_NOTES)) {
    if (hay.includes(key)) return TOPIC_NOTES[key];
  }
  const cat = String(category || "").toUpperCase();
  if (CATEGORY_NOTES[cat]) return CATEGORY_NOTES[cat];
  return GENERIC_NOTES;
};

export const PaperMarginalia = ({ slug, title, category }) => {
  const seed = slug || title || "dossier";
  const h = _hash(seed);
  const prov = useDossierProvenance(slug, title);

  const initials  = _pickN(`${seed}-init`, INITIALS, 1)[0];
  const custody   = _pickN(`${seed}-cust`, CUSTODY_UNITS, 1)[0];

  // Pull from the topic-aware pool
  const notesPool  = _resolveNotes(slug, title, category);
  // 2 to 4 notes per dossier — placed in KNOWN-SAFE slots only
  // (top-right rail + middle-right + lower-right). We deliberately
  // avoid any content-overlap slot.
  const notesCount = 2 + (h % 3);              // 2..4
  const notes      = _pickN(`${seed}-notes`, notesPool, notesCount);

  // Rotations
  const stampRot     = (((h >> 7) % 100) / 100 - 0.5) * 5;
  const stickyRot    = (((h >> 11) % 100) / 100 - 0.5) * 10;
  const filingTabRot = (((h >> 13) % 100) / 100 - 0.5) * 4;
  const fileTabRot   = (((h >> 21) % 100) / 100 - 0.5) * 1.4;
  const noteAngles   = notes.map((_, i) => (((h >> (5 + i * 3)) % 100) / 100 - 0.5) * 6);
  const notePositions = [
    { top: "58px" },
    { top: "128px" },
    { top: "198px" },
    { top: "268px" },
  ];

  // Per-dossier artifact toggles — every card gets a slightly
  // different mix so no two folders look identical.
  const hasPaperclip = h % 2 === 0;
  const hasFilingTab = (h >> 4) % 3 !== 0;
  const hasStapleHoles = (h >> 6) % 4 !== 0;
  const hasBarcode = (h >> 8) % 3 !== 0;
  const hasStickyNote = (h >> 10) % 5 === 0;
  const hasCustody = (h >> 12) % 2 === 0;
  const hasFingerprint = prov.hasFingerprint;
  const hasCircularStamp = prov.hasCircularStamp;
  const filingTabColor = ["red", "blue", "green", "yellow"][(h >> 15) % 4];

  const fileTabTitle = (title || slug || "")
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .join("-")
    .slice(0, 32);
  // Word-safe tab ref — long slugs degrade at a hyphen boundary instead of
  // a mid-word chop ("CONGRESSIONAL-STOCK-TRADING-AN…").
  const _refSlug = (() => {
    const s = (slug || "").toUpperCase();
    if (s.length <= 22) return s;
    const cut = s.slice(0, 22);
    const idx = cut.lastIndexOf("-");
    return (idx > 8 ? cut.slice(0, idx) : cut);
  })();
  const fileTabRef = `CASE FILE // REF: ${_refSlug}`;

  return (
    <>
      {/* --- TOP FILE TAB — protrudes ABOVE the folder from behind --- */}
      <span
        className="paper-file-tab"
        style={{ transform: `rotate(${fileTabRot}deg)` }}
        aria-hidden="true"
      >
        <span className="paper-file-tab__title">{fileTabTitle}</span>
        <span className="paper-file-tab__ref">{fileTabRef}</span>
      </span>

      {/* --- Chain-of-custody strip (top-right) --- */}
      {hasCustody && (
        <span className="paper-custody-strip" aria-hidden="true">
          {custody}
        </span>
      )}

      {/* --- Received-date TYPEWRITTEN stamp (bottom-right, small) ---
             Only when the dossier does NOT have the larger circular
             office stamp — otherwise it competes visually. */}
      {!hasCircularStamp && (
        <span
          className="paper-received-stamp"
          style={{ transform: `rotate(${stampRot}deg)` }}
          aria-hidden="true"
        >
          <span className="paper-received-stamp__line">REC&#39;D</span>
          <span className="paper-received-stamp__year">{prov.receivedDate.split(" ").pop()}</span>
          <span className="paper-received-stamp__initials">{initials}</span>
        </span>
      )}

      {/* --- Circular office RECEIVED stamp (bottom-right, larger) --- */}
      {hasCircularStamp && (
        <span
          className="paper-received-circle"
          aria-hidden="true"
        >
          <svg viewBox="0 0 120 120">
            <defs>
              <path id={`arc-top-${seed}`} d="M 60 60 m -46 0 a 46 46 0 1 1 92 0" fill="none" />
              <path id={`arc-bot-${seed}`} d="M 60 60 m -46 0 a 46 46 0 1 0 92 0" fill="none" />
            </defs>
            <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.9" />
            <circle cx="60" cy="60" r="46" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.85" />
            <text fontFamily="'Special Elite', 'Courier New', monospace" fontSize="9" letterSpacing="1.6" fill="currentColor" opacity="0.95">
              <textPath href={`#arc-top-${seed}`} startOffset="50%" textAnchor="middle">
                {prov.receivedOffice}
              </textPath>
            </text>
            <text x="60" y="58" textAnchor="middle" fontFamily="'Special Elite', 'Courier New', monospace" fontSize="12" fontWeight="700" letterSpacing="1.4" fill="currentColor">
              RECEIVED
            </text>
            <text x="60" y="74" textAnchor="middle" fontFamily="'Special Elite', 'Courier New', monospace" fontSize="9" letterSpacing="1.6" fill="currentColor">
              {prov.receivedDate}
            </text>
            <text fontFamily="'Special Elite', 'Courier New', monospace" fontSize="9" letterSpacing="1.8" fill="currentColor" opacity="0.95">
              <textPath href={`#arc-bot-${seed}`} startOffset="50%" textAnchor="middle">
                {prov.receivedCity}
              </textPath>
            </text>
          </svg>
        </span>
      )}

      {/* --- Fingerprint smudge (deterministic subset) --- */}
      {hasFingerprint && (
        <span className="paper-fingerprint" aria-hidden="true" />
      )}

      {/* --- Filing tab (right edge, protruding) — REMOVED
              Feb 2026: this was a 26 × 130 px `linear-gradient`
              strip labelled ARCHIVE-BOX along the right edge of
              the dossier paper. Read as an unintended vertical
              overlay band. Kept archival provenance is now
              carried by the kraft `paper-archive-box-card` next
              to the DECLASSIFIED stamp inside the folder. */}
      {false && hasFilingTab && (
        <span
          className={`paper-filing-tab paper-filing-tab--${filingTabColor}`}
          style={{ transform: `translateY(-50%) rotate(${filingTabRot}deg)` }}
          aria-hidden="true"
        >
          {`ARCHIVE-BOX ${prov.catalog}`}
        </span>
      )}

      {/* --- Paperclip rust impression (top-left) --- */}
      {hasPaperclip && (
        <span className="paper-clip-impression" aria-hidden="true" />
      )}

      {/* --- Staple holes (top edge, near paperclip) --- */}
      {hasStapleHoles && (
        <span className="paper-staple-holes" aria-hidden="true">
          <span /><span />
        </span>
      )}

      {/* --- Barcode label (bottom-left corner) --- */}
      {hasBarcode && (
        <span className="paper-barcode" aria-hidden="true">
          <span className="paper-barcode__bars" />
          <span className="paper-barcode__num">{String(h % 900000 + 100000)}</span>
        </span>
      )}

      {/* --- Yellow sticky note (rare) --- */}
      {hasStickyNote && (
        <span
          className="paper-sticky-note"
          style={{ transform: `rotate(${stickyRot}deg)` }}
          aria-hidden="true"
        >
          {notes[0] || "Review pending."}
        </span>
      )}

      {/* --- Handwritten investigator notes (topic-aware) --- */}
      {notes.map((text, i) => {
        const pos = notePositions[i] || notePositions[notePositions.length - 1];
        return (
          <span
            key={i}
            className="paper-marginalia"
            style={{
              ...pos,
              transform: `rotate(${noteAngles[i]}deg)`,
            }}
            aria-hidden="true"
          >
            {text}
          </span>
        );
      })}
    </>
  );
};

export default PaperMarginalia;
